import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { Star, MessageSquare, Send, Utensils, BookOpen, Info, MapPin, Tag } from 'lucide-react';
import MapView from './MapView';

export default function RestaurantDetail({ restaurant, user, onBack }) {
    const [reviews, setReviews] = useState([]);
    const [newReview, setNewReview] = useState('');
    const [newRating, setNewRating] = useState(5);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('main');
    const [mainImage, setMainImage] = useState(restaurant.imageUrl);
    const appId = 'goeatout-app';

    useEffect(() => {
        setMainImage(restaurant.imageUrl);
        setActiveTab('main'); // Reset to main tab when restaurant changes
    }, [restaurant]);

    useEffect(() => {
        if (!restaurant) return;
        const reviewsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/reviews`),
            where("restaurantId", "==", restaurant.id)
        );
        const unsubscribe = onSnapshot(reviewsQuery, (snapshot) => {
            setReviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [restaurant, appId]);

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        if (!newReview.trim()) return;
        if (!user || user.isAnonymous) {
            setError('You must be logged in to leave a review.');
            return;
        }
        setError('');
        try {
            await addDoc(collection(db, `/artifacts/${appId}/public/data/reviews`), {
                restaurantId: restaurant.id,
                userId: user.uid,
                userName: user.email,
                text: newReview,
                rating: newRating,
                createdAt: serverTimestamp()
            });
            setNewReview('');
            setNewRating(5);
        } catch (err) {
            setError('Failed to submit review.');
            console.error(err);
        }
    };

    const TabButton = ({ tabName, label, icon: Icon }) => (
        <button onClick={() => setActiveTab(tabName)} className={`flex items-center gap-2 px-4 py-3 font-semibold border-b-4 transition-colors ${activeTab === tabName ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-blue-600'}`}>
            <Icon size={18} /> {label}
        </button>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'menu':
                return (
                    <div className="p-8 animate-fade-in-up">
                        <h3 className="text-2xl font-bold mb-4">Sample Menu</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h4 className="text-xl font-semibold text-gray-700 border-b pb-2 mb-2">Starters</h4>
                                <ul className="space-y-1">{restaurant.menu?.starters.map(item => <li key={item.name} className="flex justify-between"><span>{item.name}</span><span>{item.price}</span></li>)}</ul>
                            </div>
                            <div>
                                <h4 className="text-xl font-semibold text-gray-700 border-b pb-2 mb-2">Mains</h4>
                                <ul className="space-y-1">{restaurant.menu?.mains.map(item => <li key={item.name} className="flex justify-between"><span>{item.name}</span><span>{item.price}</span></li>)}</ul>
                            </div>
                            <div>
                                <h4 className="text-xl font-semibold text-gray-700 border-b pb-2 mb-2">Desserts</h4>
                                <ul className="space-y-1">{restaurant.menu?.desserts.map(item => <li key={item.name} className="flex justify-between"><span>{item.name}</span><span>{item.price}</span></li>)}</ul>
                            </div>
                        </div>
                    </div>
                );
            case 'reviews':
                return (
                       <div className="p-8 animate-fade-in-up">
                            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2"><MessageSquare/> Reviews</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    {reviews.length > 0 ? reviews.map(review => (
                                        <div key={review.id} className="bg-gray-50 p-4 rounded-lg border">
                                            <div className="flex items-center mb-1">{[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500' : 'text-gray-300'}`} fill="currentColor"/>)}</div>
                                            <p className="text-gray-700">{review.text}</p>
                                            <p className="text-xs text-gray-500 mt-2">- {review.userName} on {new Date(review.createdAt?.toDate()).toLocaleDateString()}</p>
                                        </div>
                                    )) : <p>No reviews yet. Be the first!</p>}
                                </div>
                                <form onSubmit={handleReviewSubmit} className="border-t md:border-t-0 md:border-l md:pl-8 pt-6 md:pt-0">
                                   <h4 className="text-xl font-semibold mb-2">Leave a Review</h4>
                                   {user && !user.isAnonymous ? (
                                       <>
                                           <div className="flex items-center mb-2">{[...Array(5)].map((_, i) => <Star key={i} className={`w-6 h-6 cursor-pointer ${i < newRating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" onClick={() => setNewRating(i + 1)}/>)}</div>
                                           <textarea value={newReview} onChange={(e) => setNewReview(e.target.value)} placeholder="Share your experience..." className="w-full p-2 border rounded-md" rows="3"></textarea>
                                           <button type="submit" className="mt-2 bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center gap-2"><Send size={16}/> Submit Review</button>
                                       </>
                                   ) : <p className="text-gray-600">Please <button onClick={onBack} className="font-semibold text-blue-600 hover:underline">log in</button> to leave a review.</p>}
                                   {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                                </form>
                            </div>
                        </div>
                );
            case 'about':
                return (
                    <div className="p-8 animate-fade-in-up">
                        <h3 className="text-2xl font-bold mb-4">About {restaurant.name}</h3>
                        <p className="text-gray-600 mb-6">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed doeiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
                        <div className="h-80 rounded-lg overflow-hidden shadow-md">
                           <MapView restaurants={[restaurant]} onRestaurantClick={() => {}} />
                        </div>
                    </div>
                );
            default: // main tab
                return (
                    <div className="p-8 animate-fade-in-up">
                        <h3 className="text-2xl font-bold mb-4 flex items-center gap-2"><Tag/> Vouchers & Offers</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(restaurant.vouchers || []).map(voucher => (
                                <div key={voucher.id} className="bg-green-50 text-green-800 p-4 rounded-lg border border-green-200">
                                    <p className="font-bold text-lg">{voucher.description}</p>
                                    <p className="text-sm">{voucher.details}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="animate-fade-in-up">
            <button onClick={onBack} className="mb-4 font-semibold text-blue-600 hover:underline">
                &larr; Back to all restaurants
            </button>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <img src={mainImage} alt={`Cuisine at ${restaurant.name}`} className="w-full h-80 object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/1200x400/cccccc/ffffff?text=Image+Not+Found'; }} />
                
                {restaurant.gallery && restaurant.gallery.length > 0 && (
                    <div className="p-2 bg-gray-100 grid grid-cols-3 sm:grid-cols-6 gap-2">
                        {restaurant.gallery.map((imgUrl, index) => (
                            <img 
                                key={index} 
                                src={imgUrl} 
                                // FIX: Removed the redundant word "image" from the alt text to fix the accessibility error.
                                alt={`Gallery thumbnail ${index + 1}`} 
                                className="w-full h-24 object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setMainImage(imgUrl)}
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/cccccc/ffffff?text=Image'; }}
                            />
                        ))}
                    </div>
                )}

                <div className="p-8">
                    <h1 className="text-4xl font-extrabold text-gray-900">{restaurant.name}</h1>
                    <p className="text-lg text-gray-500 mt-1 flex items-center gap-2"><MapPin size={16}/> {restaurant.address}</p>
                </div>

                <div className="border-b border-gray-200">
                    <nav className="flex space-x-2 px-8">
                        <TabButton tabName="main" label="Main" icon={Utensils} />
                        <TabButton tabName="menu" label="Menu" icon={BookOpen} />
                        <TabButton tabName="reviews" label="Reviews" icon={MessageSquare} />
                        <TabButton tabName="about" label="About" icon={Info} />
                    </nav>
                </div>
                
                {renderTabContent()}
            </div>
        </div>
    );
}
